import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { CalendarClock, MapPin, CheckCircle2, Clock, AlertCircle, Package } from "lucide-react";
import useForceLightTheme from "../lib/useForceLightTheme";

/**
 * PublicBookingPage
 * -----------------
 * Customer-facing landing page a shop shares with their customer AFTER a
 * quote is accepted. Shows what the customer actually needs:
 *   • Which vehicle(s) are booked and what work is being done
 *   • Scheduled dates and workshop location
 *   • Current progress phase ("Vehicle received" → "Installation in
 *     progress" → "Ready for collection")
 *   • Branding (logo, phone, email)
 *
 * Prices, internal notes, assigned installer names and inventory details
 * are intentionally stripped on the backend before this page renders.
 */

const INSTALL_LABEL = {
  wrap_colour_change:         "Vinyl Wrap — Full Colour Change",
  wrap_business:              "Vinyl Wrap — Print / Branding",
  wrap_interior:              "Vinyl Wrap — Interior",
  wrap_partial:               "Vinyl Wrap — Partial",
  wrap_chrome_delete:         "Chrome Delete",
  ppf_partial:                "PPF — Partial",
  ppf_full_front:             "PPF — Front Coverage",
  ppf_full_body:              "PPF — Full Body",
  ppf_colour:                 "Colour PPF — Full Body",
  ppf_colour_partial:         "Colour PPF — Partial",
  tinting:                    "Window Tinting",
  ceramic:                    "Ceramic Coating",
  detail_full:                "Detail — Full Vehicle",
  detail_exterior:            "Detail — Exterior",
  detail_interior:            "Detail — Interior",
  detail_paint_correction:    "Detail — Paint Correction",
  detail_engine_bay:          "Detail — Engine Bay",
  detail_headlight_restoration: "Detail — Headlight Restoration",
  signage_decals:             "Signage / Decals",
  body_kit_install:           "Body kit installation",
  lens_tint:                  "Headlight / Taillight Tint",
  badge_modification:         "Badge Modification",
};

const PHASE_META = {
  scheduling:  { icon: Clock,         accent: "text-blue-300 bg-blue-500/10 border-blue-400/30",       label: "Scheduling" },
  in_workshop: { icon: Package,       accent: "text-amber-300 bg-amber-500/10 border-amber-400/30",    label: "In the workshop" },
  done:        { icon: CheckCircle2,  accent: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30", label: "Ready" },
  cancelled:   { icon: AlertCircle,   accent: "text-red-300 bg-red-500/10 border-red-400/30",          label: "Cancelled" },
};

function prettyDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "long", year: "numeric" });
  } catch { return d; }
}

export default function PublicBookingPage() {
  useForceLightTheme();
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API_BASE}/public/booking/${token}`)
      .then(({ data }) => setBooking(data))
      .catch((e) => setError(e?.response?.data?.detail || "Booking not found"));
  }, [token]);

  if (error) {
    return (
      <div className="wc-public-light min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-md text-center space-y-3">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
          <h1 className="text-slate-900 text-2xl font-semibold">{error}</h1>
          <p className="text-slate-500 text-sm">Reach out to the shop if you think this link should work.</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="wc-public-light min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-sm">Loading your booking…</div>
      </div>
    );
  }

  const biz = booking.business || {};
  const phaseMeta = PHASE_META[booking.phase] || PHASE_META.scheduling;
  const PhaseIcon = phaseMeta.icon;
  const hasRange = booking.scheduled_date && booking.scheduled_end_date && booking.scheduled_date !== booking.scheduled_end_date;

  return (
    <div className="wc-public-light min-h-screen bg-slate-50 text-slate-900 py-8 md:py-12 px-4" data-testid="public-booking-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* ---- Branded header ---- */}
        <div className="flex items-center gap-3">
          {biz.logo_url ? (
            <img src={biz.logo_url} alt={biz.name} className="h-10 rounded-lg object-contain bg-white/5 p-1" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white font-semibold">
              {(biz.name || "W").slice(0, 1)}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-slate-500">Booking summary</div>
            <div className="text-white font-semibold truncate">{biz.name}</div>
          </div>
        </div>

        {/* ---- Hero card ---- */}
        <div className="bg-white/[0.04] border border-white/10 rounded-3xl p-6 md:p-7 space-y-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Prepared for</div>
              <h1 className="text-white text-2xl md:text-3xl font-semibold">{booking.contact_name || "Customer"}</h1>
              {Number(booking.amount) > 0 && (
                <div className="mt-3" data-testid="public-booking-amount">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">Total</div>
                  <div className="text-white text-3xl md:text-4xl font-semibold tabular-nums">
                    ${Number(booking.amount).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
            <div
              data-testid="public-booking-phase"
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border ${phaseMeta.accent}`}
            >
              <PhaseIcon className="w-3.5 h-3.5" />
              {booking.phase_label || phaseMeta.label}
            </div>
          </div>

          {/* Schedule + Location grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500">
                <CalendarClock className="w-3.5 h-3.5" /> Scheduled
              </div>
              <div className="text-white mt-2" data-testid="public-booking-dates">
                {booking.scheduled_date ? (
                  hasRange ? (
                    <>
                      <div>{prettyDate(booking.scheduled_date)}</div>
                      <div className="text-slate-400 text-sm">to {prettyDate(booking.scheduled_end_date)}</div>
                    </>
                  ) : (
                    prettyDate(booking.scheduled_date)
                  )
                ) : (
                  <span className="text-slate-500">To be confirmed</span>
                )}
              </div>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500">
                <MapPin className="w-3.5 h-3.5" /> Where
              </div>
              <div className="text-white mt-2 text-sm">
                {booking.location_type === "off_site" ? (
                  booking.location_address || "At your location"
                ) : (
                  <>
                    <div>In our workshop</div>
                    {biz.contact_phone && <div className="text-slate-400 text-xs mt-1">{biz.contact_phone}</div>}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ---- Vehicles + installations ---- */}
        {Array.isArray(booking.job_items) && booking.job_items.length > 0 && (
          <div className="space-y-4" data-testid="public-booking-vehicles">
            {booking.job_items.map((item, vi) => {
              const hasPrices = (item.installations || []).some((i) => Number(i.price) > 0);
              return (
              <div
                key={item.id || vi}
                data-testid={`public-booking-vehicle-${vi}`}
                className="bg-white/[0.04] border border-white/10 rounded-3xl p-5 md:p-6 space-y-4"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">
                      Vehicle {booking.job_items.length > 1 ? `${vi + 1} of ${booking.job_items.length}` : ""}
                    </div>
                    <div className="text-white text-lg font-semibold mt-0.5">{item.vehicle}</div>
                    {item.phase_label && (
                      <div className="mt-1.5 text-xs text-slate-400">
                        Status: <span className="text-slate-200">{item.phase_label}</span>
                      </div>
                    )}
                  </div>
                  {hasPrices && (
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-widest text-slate-500">Subtotal</div>
                      <div
                        data-testid={`public-booking-vehicle-${vi}-subtotal`}
                        className="text-white text-lg font-semibold tabular-nums"
                      >
                        ${Number(item.subtotal || 0).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>

                {(item.installations || []).length > 0 && (
                  <ul className="space-y-3">
                    {item.installations.map((inst, i) => {
                      const areas = [...(inst.wrap_areas || []), ...(inst.ppf_areas || [])];
                      return (
                        <li key={i} className="border-l-2 border-white/10 pl-3">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="text-white text-sm font-medium">
                              {INSTALL_LABEL[inst.installation_type] || inst.installation_type}
                            </div>
                            {Number(inst.price) > 0 && (
                              <div
                                data-testid={`public-booking-vehicle-${vi}-install-${i}-price`}
                                className="text-white text-sm font-semibold tabular-nums"
                              >
                                ${Number(inst.price).toLocaleString()}
                              </div>
                            )}
                          </div>
                          {areas.length > 0 && (
                            <div className="text-sm text-slate-300 mt-1.5 flex gap-2">
                              <span className="text-[10px] uppercase tracking-widest text-slate-500 shrink-0 pt-0.5 min-w-[70px]">Areas</span>
                              <span>{areas.join(", ")}</span>
                            </div>
                          )}
                          {inst.material && (
                            <div className="text-sm text-slate-300 mt-1 flex gap-2">
                              <span className="text-[10px] uppercase tracking-widest text-slate-500 shrink-0 pt-0.5 min-w-[70px]">Material</span>
                              <span>{inst.material}</span>
                            </div>
                          )}
                          {inst.notes && (
                            <div className="text-sm text-slate-300 mt-1 flex gap-2">
                              <span className="text-[10px] uppercase tracking-widest text-slate-500 shrink-0 pt-0.5 min-w-[70px]">Notes</span>
                              <span className="whitespace-pre-wrap">{inst.notes}</span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              );
            })}
          </div>
        )}

        {/* ---- T&Cs nudge ---- */}
        {booking.terms_pending && (
          <div className="bg-amber-500/10 border border-amber-400/30 rounded-2xl p-4 text-sm text-amber-200">
            <div className="font-medium text-amber-100 mb-1">Installation terms to sign</div>
            Our installation terms &amp; conditions are ready for your signature. The shop will send you a link to review and sign them before work begins.
          </div>
        )}

        {/* ---- Shop contact ---- */}
        <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-5 md:p-6">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Need to make changes?</div>
          <div className="text-white font-medium">{biz.name}</div>
          <div className="text-sm text-slate-300 mt-1 space-y-0.5">
            {biz.contact_phone && <div>📞 {biz.contact_phone}</div>}
            {biz.contact_email && <div>✉️ {biz.contact_email}</div>}
          </div>
        </div>

        <div className="text-center text-xs text-slate-500 pt-2 pb-6">
          Updated {prettyDate(booking.updated_at)} · powered by WrapConsole
        </div>
      </div>
    </div>
  );
}
